import {
  C3D_CELL,
  C3D_GARMENT_COLORS,
  itemEquip,
  c3dTopSheet,
  c3dBottomSheet,
  c3dShoesSheet,
  c3dHatSheet,
  c3dMaskSheet,
  c3dBackSheet,
} from "@ghostland/shared";
import { recolorByLuminance } from "../sprites/c3dComposite.js";
import { loadPartImage } from "../sprites/c3dImage.js";
import { drawItemIcon } from "./itemArt.js";

/**
 * Item thumbnails. Garments (anything with an equip mapping) render their actual FRONT clothing
 * layer — the front idle cell of the c3d sheet, recoloured to the item's colourway (exactly how
 * it appears worn). Everything else (ores, scroll, pickaxe…) keeps its procedural icon. Used by
 * the inventory grid and the THREADS shop so clothes show a real picture instead of a "?".
 */

const THUMB = 48; // native px of the square thumbnail canvas (scaled by CSS, pixelated)
const garmentCache = new Map<string, HTMLCanvasElement>();

function sheetFor(slot: string, styleId: number): string | null {
  switch (slot) {
    case "top": return c3dTopSheet(styleId);
    case "bottom": return c3dBottomSheet(styleId);
    case "shoes": return c3dShoesSheet(styleId);
    case "hat": return c3dHatSheet(styleId);
    case "mask": return c3dMaskSheet(styleId);
    case "back": return c3dBackSheet(styleId);
    default: return null;
  }
}

/** Crop a canvas to the bounding box of its opaque pixels (so the garment fills the thumb). */
function cropOpaque(src: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = src.getContext("2d", { willReadFrequently: true })!;
  const { data } = ctx.getImageData(0, 0, src.width, src.height);
  let minX = src.width, maxX = -1, minY = src.height, maxY = -1;
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      if (data[(y * src.width + x) * 4 + 3] > 16) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return src;
  const w = maxX - minX + 1, h = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = w; out.height = h;
  const octx = out.getContext("2d")!; octx.imageSmoothingEnabled = false;
  octx.drawImage(src, minX, minY, w, h, 0, 0, w, h);
  return out;
}

/** The recoloured front clothing layer for a garment item, cropped tight. Cached. */
export async function composeGarmentThumb(itemId: string): Promise<HTMLCanvasElement | null> {
  const cached = garmentCache.get(itemId);
  if (cached) return cached;
  const eq = itemEquip(itemId);
  if (!eq) return null;
  const sheet = sheetFor(eq.slot, eq.styleId);
  if (!sheet) return null;
  const img = await loadPartImage(`/assets/${sheet}`);
  if (!img) return null;

  // Front-facing idle frame 0 lives in cell (col 0, row 0): source rect (0,0,144,144).
  const cell = document.createElement("canvas");
  cell.width = C3D_CELL; cell.height = C3D_CELL;
  const cctx = cell.getContext("2d")!; cctx.imageSmoothingEnabled = false;
  cctx.drawImage(img, 0, 0, C3D_CELL, C3D_CELL, 0, 0, C3D_CELL, C3D_CELL);

  // colorId 0 = draw the sheet as rendered; otherwise recolour by the colourway ramp.
  const ramp = eq.colorId > 0 ? C3D_GARMENT_COLORS[eq.colorId]?.ramp : undefined;
  const recoloured = ramp ? recolorByLuminance(cell, ramp) : cell;
  const thumb = cropOpaque(recoloured);
  garmentCache.set(itemId, thumb);
  return thumb;
}

/**
 * A square thumbnail canvas for an item. Returns immediately; for garments the recoloured front
 * layer is composited asynchronously and drawn in (contained, centred) once ready. Non-garments
 * use their procedural icon.
 */
export function itemThumb(itemId: string): HTMLCanvasElement {
  if (!itemEquip(itemId)) return drawItemIcon(itemId);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = THUMB;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  void composeGarmentThumb(itemId).then((g) => {
    if (!g) { ctx.drawImage(drawItemIcon(itemId), 0, 0, THUMB, THUMB); return; }
    const scale = Math.min(THUMB / g.width, THUMB / g.height) * 0.92; // a little padding
    const w = Math.round(g.width * scale), h = Math.round(g.height * scale);
    ctx.clearRect(0, 0, THUMB, THUMB);
    ctx.drawImage(g, Math.round((THUMB - w) / 2), Math.round((THUMB - h) / 2), w, h);
  });
  return canvas;
}
